import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { COLORS, MONTHS_IT, fmtEur, fmtEurCompact } from "./eaFormat";
import { HatchDefs } from "./EaUi";

function ytdLabel(block) {
  if (!block || block.points === 0) return null;
  return block.last_invoiced_month ? `fatture fino a ${MONTHS_IT[block.last_invoiced_month - 1]} (${block.months_invoiced}/12)` : "nessuna fattura";
}

function BfTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs space-y-1">
      <div className="font-semibold text-slate-900">{d.name}</div>
      <div>Budget annuale: {fmtEur(d.budget)}</div>
      <div>Forecast annuale: {fmtEur(d.forecast)}</div>
      <div>Spesa fatturata YTD (parziale): {fmtEur(d.ytd)}</div>
      {d.note && <div className="text-slate-500">{d.note}</div>}
    </div>
  );
}

// Colori per commodity come negli altri grafici: budget tenue, forecast pieno, spesa tratteggiata.
const TONES = { Power: { c: COLORS.power, light: COLORS.powerLight, hatch: "ea-hatch-p" }, Gas: { c: COLORS.gas, light: COLORS.gasLight, hatch: "ea-hatch-g" }, Totale: { c: COLORS.total, light: COLORS.totalLight, hatch: "ea-hatch-t" } };
function BfLegend() {
  const items = [["Budget annuale (tinta chiara)", { background: COLORS.totalLight }], ["Forecast annuale (tinta piena)", { background: COLORS.total }],
    ["Spesa fatturata YTD (tratteggiata)", { background: `repeating-linear-gradient(45deg, ${COLORS.total} 0 2px, #fff 2px 5px)`, border: `1px solid ${COLORS.total}` }]];
  return (
    <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-600 pt-2" data-testid="ea-bf-legend">
      {items.map(([t, st]) => <span key={t} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={st} />{t}</span>)}
      <span className="text-slate-400">· Power ambra, Gas blu, Totale viola</span>
    </div>
  );
}

export function EaBudgetForecastChart({ data }) {
  const rows = [
    data.power.points > 0 && { name: "Power", budget: data.power.budget, forecast: data.power.forecast, ytd: data.power.spend_ytd, note: ytdLabel(data.power) },
    data.gas.points > 0 && { name: "Gas", budget: data.gas.budget, forecast: data.gas.forecast, ytd: data.gas.spend_ytd, note: ytdLabel(data.gas) },
    data.power.points > 0 && data.gas.points > 0 && { name: "Totale", budget: data.budget_total, forecast: data.forecast_total, ytd: data.spend_total_ytd },
  ].filter(Boolean);
  return (
    <div className="h-72" data-testid="ea-chart-budget-forecast">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={4}>
          <HatchDefs id="ea-hatch-p" color={COLORS.power} />
          <HatchDefs id="ea-hatch-g" color={COLORS.gas} />
          <HatchDefs id="ea-hatch-t" color={COLORS.total} />
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtEurCompact} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={72} />
          <Tooltip content={<BfTooltip />} cursor={{ fill: "#F1F5F9" }} />
          <Legend content={<BfLegend />} />
          <Bar dataKey="budget" name="Budget annuale" radius={[4, 4, 0, 0]}>{rows.map((r) => <Cell key={r.name} fill={TONES[r.name].light} />)}</Bar>
          <Bar dataKey="forecast" name="Forecast annuale" radius={[4, 4, 0, 0]}>{rows.map((r) => <Cell key={r.name} fill={TONES[r.name].c} />)}</Bar>
          <Bar dataKey="ytd" name="Spesa fatturata YTD (parziale)" radius={[4, 4, 0, 0]}>{rows.map((r) => <Cell key={r.name} fill={`url(#${TONES[r.name].hatch})`} stroke={TONES[r.name].c} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
